import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { DollarSign, Plus, Trash2, Edit, Building2, Calendar, Sparkles, TrendingUp } from 'lucide-react';
import { toast } from 'sonner';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { useIsMobile } from '../hooks/useIsMobile';
import { notifyDataChanged } from '../utils/dataBus';

const API = `${process.env.REACT_APP_BACKEND_URL || window.location.origin}/api`;

const Depots = () => {
  const { user } = useAuth();
  const isMobile = useIsMobile(640);
  const [depots, setDepots] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [selectedDepot, setSelectedDepot] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [formData, setFormData] = useState({
    montant: '',
    date: new Date().toISOString().split('T')[0],
    banque: '',
    note: ''
  });
  
  // Filtrage mensuel
  const [selectedMonth, setSelectedMonth] = useState('current'); // 'current', 'previous' ou 'custom'
  const [customMonth, setCustomMonth] = useState(null); // { year: 2024, month: 10 }
  const [showMonthDropdown, setShowMonthDropdown] = useState(false);

  const getMonthDates = (monthType) => {
    const now = new Date();
    let start, end;
    
    if (monthType === 'current') {
      start = new Date(now.getFullYear(), now.getMonth(), 1);
      end = new Date(now.getFullYear(), now.getMonth() + 1, 0, 23, 59, 59);
    } else if (monthType === 'previous') {
      start = new Date(now.getFullYear(), now.getMonth() - 1, 1);
      end = new Date(now.getFullYear(), now.getMonth(), 0, 23, 59, 59);
    } else if (monthType === 'custom' && customMonth) {
      start = new Date(customMonth.year, customMonth.month - 1, 1);
      end = new Date(customMonth.year, customMonth.month, 0, 23, 59, 59);
    }
    
    return { start, end };
  };

  const getAvailableMonths = () => {
    const months = [];
    const now = new Date();
    const currentMonth = now.getMonth() + 1;
    const currentYear = now.getFullYear();
    const previousMonth = currentMonth === 1 ? 12 : currentMonth - 1;
    const previousYear = currentMonth === 1 ? currentYear - 1 : currentYear;
    
    for (let i = 0; i < 12; i++) {
      const targetDate = new Date(currentYear, currentMonth - 1 - i, 1);
      const year = targetDate.getFullYear();
      const month = targetDate.getMonth() + 1;
      
      if ((year === currentYear && month === currentMonth) || 
          (year === previousYear && month === previousMonth)) {
        continue;
      }
      
      months.push({
        year,
        month,
        label: targetDate.toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' })
      });
    }
    
    return months.reverse();
  };

  useEffect(() => {
    fetchDepots();
  }, [selectedMonth]);

  const fetchDepots = async () => {
    try {
      const response = await axios.get(`${API}/depots-bancaires`, { withCredentials: true });
      let allDepots = response.data || [];
      
      // Filtrage mensuel
      if (selectedMonth) {
        const { start, end } = getMonthDates(selectedMonth);
        allDepots = allDepots.filter(depot => {
          const depotDate = new Date(depot.date);
          return depotDate >= start && depotDate <= end;
        });
      }
      
      // Tri par date (plus récents en premier)
      allDepots.sort((a, b) => new Date(b.date) - new Date(a.date));
      
      setDepots(allDepots);
    } catch (error) {
      console.error('Erreur:', error);
      toast.error('Erreur lors du chargement des dépôts');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (isEditing && selectedDepot) {
        // Mode édition - PUT request
        await axios.put(`${API}/depots-bancaires/${selectedDepot.id}`, {
          ...formData,
          montant: parseInt(formData.montant),
          date: new Date(formData.date).toISOString()
        }, { withCredentials: true });
        
        toast.success('Dépôt modifié avec succès');
      } else {
        // Mode création - POST request
        await axios.post(`${API}/depots-bancaires`, {
          ...formData,
          montant: parseInt(formData.montant),
          date: new Date(formData.date).toISOString()
        }, { withCredentials: true });
        
        toast.success('Dépôt bancaire enregistré');
      }
      notifyDataChanged('depot');
      
      setIsDialogOpen(false);
      setIsEditing(false);
      setSelectedDepot(null);
      setFormData({
        montant: '',
        date: new Date().toISOString().split('T')[0],
        banque: '',
        note: ''
      });
      fetchDepots();
    } catch (error) {
      console.error('Erreur:', error);
      toast.error(isEditing ? 'Erreur lors de la modification' : 'Erreur lors de l\'enregistrement');
    }
  };

  const handleEdit = (depot) => {
    setIsEditing(true);
    setSelectedDepot(depot);
    setFormData({
      montant: depot.montant.toString(),
      date: new Date(depot.date).toISOString().split('T')[0],
      banque: depot.banque || '',
      note: depot.note || ''
    });
    setIsDialogOpen(true);
  };

  const handleAddNew = () => {
    setIsEditing(false);
    setSelectedDepot(null);
    setFormData({
      montant: '',
      date: new Date().toISOString().split('T')[0],
      banque: '',
      note: ''
    });
    setIsDialogOpen(true);
  };

  const handleDelete = async () => {
    if (!selectedDepot) return;
    
    try {
      await axios.delete(`${API}/depots-bancaires/${selectedDepot.id}`, { withCredentials: true });
      toast.success('Dépôt supprimé avec succès');
      notifyDataChanged('depot');
      setShowDeleteDialog(false);
      setSelectedDepot(null);
      fetchDepots();
    } catch (error) {
      console.error('Erreur:', error);
      toast.error('Erreur lors de la suppression');
    }
  };

  // Calculer le total des dépôts
  const totalDepots = depots.reduce((sum, depot) => sum + depot.montant, 0);

  return (
    <div className="space-y-6 pb-20 xl:pb-8" data-testid="depots-page">
      {/* Header Ultra Wahoooo - RESPONSIVE */}
      <div className="relative overflow-hidden">
        {/* Animated background glow */}
        <div className="absolute -inset-2 bg-gradient-to-r from-purple-400/20 via-pink-400/20 to-purple-400/20 rounded-2xl blur-3xl animate-pulse"></div>
        
        <div className="relative backdrop-blur-xl bg-gradient-to-br from-[#1C2541]/95 via-[#2A3B5C]/95 to-[#1C2541]/95 p-4 sm:p-6 rounded-xl sm:rounded-2xl border-2 border-purple-400/40 shadow-2xl">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 sm:gap-4">
            <div className="space-y-1 sm:space-y-2">
              <h1 className="text-2xl sm:text-3xl lg:text-4xl xl:text-5xl font-black text-transparent bg-clip-text bg-gradient-to-r from-purple-400 via-pink-300 to-purple-400 drop-shadow-[0_0_30px_rgba(192,132,252,0.5)] animate-pulse">
                <Sparkles className="inline-block w-6 h-6 sm:w-8 sm:h-8 mr-2 text-purple-400 animate-spin" style={{ animationDuration: '3s' }} />
                Dépôts Bancaires
              </h1>
              <p className="text-purple-200/80 text-sm sm:text-base lg:text-lg font-semibold">Gestion luxueuse de vos dépôts</p>
            </div>
            <Button
              onClick={handleAddNew}
              className="w-full sm:w-auto bg-gradient-to-r from-purple-500 via-pink-500 to-purple-500 hover:from-purple-600 hover:via-pink-600 hover:to-purple-600 text-white font-black text-base sm:text-lg px-6 sm:px-8 py-4 sm:py-6 shadow-2xl shadow-purple-400/50 hover:scale-105 sm:hover:scale-110 hover:rotate-1 transition-all duration-300 border-2 border-purple-300/50"
            >
              <Plus className="w-5 h-5 sm:w-6 sm:h-6 mr-2" />
              Nouveau Dépôt
            </Button>
          </div>
        </div>
      </div>

      {/* Filtrage Mensuel ULTRA PREMIUM - RESPONSIVE */}
      <div className="relative">
        <div className="absolute inset-0 bg-gradient-to-r from-purple-400/20 via-pink-400/20 to-purple-400/20 rounded-2xl sm:rounded-3xl blur-2xl animate-pulse"></div>
        <Card className="relative glass-effect border-2 sm:border-3 border-purple-400/50 shadow-2xl">
          <CardContent className="p-3 sm:p-4 md:p-6">
            <div className="flex flex-col sm:flex-row flex-wrap items-stretch sm:items-center justify-center gap-2 sm:gap-3">
              {/* Dropdown Premium "Choisir Un Mois" - RESPONSIVE */}
              <div className="relative w-full sm:w-auto">
                <button
                  onClick={() => setShowMonthDropdown(!showMonthDropdown)}
                  className={`w-full sm:w-auto px-4 sm:px-6 py-3 sm:py-4 rounded-xl sm:rounded-2xl font-black text-sm sm:text-base transition-all duration-300 transform hover:scale-105 ${
                    selectedMonth === 'custom'
                      ? 'bg-gradient-to-r from-purple-500 via-pink-500 to-purple-500 text-white shadow-[0_0_40px_rgba(168,85,247,0.6)] scale-105'
                      : 'bg-gradient-to-br from-[#1C2541] to-[#2A3B5C] text-[#EAEAEA] border-2 border-purple-400/30 hover:border-purple-400/60'
                  }`}
                >
                  <span className="block sm:inline">📋 {customMonth ? new Date(customMonth.year, customMonth.month - 1).toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' }) : 'Choisir Un Mois'}</span>
                  <div className="text-xs font-normal mt-1 opacity-80">
                    {customMonth ? 'Période sélectionnée' : 'Sélectionner...'}
                  </div>
                </button>
              </div>

              {/* Calendrier des Mois - CENTRÉ ET VISIBLE */}
              {showMonthDropdown && (
                <>
                  <div 
                    className="fixed inset-0 bg-black/70 backdrop-blur-sm z-[99998] animate-in fade-in duration-200 flex items-center justify-center p-4" 
                    onClick={() => setShowMonthDropdown(false)}
                  >
                    <div 
                      className="w-full max-w-[90vw] sm:max-w-[600px] md:max-w-[700px] max-h-[90vh] overflow-y-auto z-[99999] animate-in zoom-in-95 duration-300"
                      onClick={(e) => e.stopPropagation()}
                    >
                      {/* Glow Effect */}
                      <div className="absolute inset-0 bg-gradient-to-r from-purple-400 via-pink-400 to-purple-400 rounded-3xl blur-2xl opacity-30 animate-pulse pointer-events-none"></div>
                      
                      <div className="relative bg-gradient-to-br from-[#1C2541] via-[#2A3B5C] to-[#1C2541] border-3 border-purple-400/60 rounded-3xl shadow-2xl overflow-hidden">
                        {/* Header Ultra Premium */}
                        <div className="px-4 sm:px-6 py-4 border-b-2 border-purple-400/40 bg-gradient-to-r from-purple-400/10 to-pink-400/10 sticky top-0 z-20 backdrop-blur-sm">
                        <div className="flex items-center justify-between">
                          <div>
                            <h3 className="text-xl sm:text-2xl font-black text-transparent bg-clip-text bg-gradient-to-r from-purple-300 to-pink-200">
                              📅 Choisir Un Mois
                            </h3>
                            <p className="text-purple-200/80 text-xs sm:text-sm mt-1">Sélectionnez le mois souhaité</p>
                          </div>
                          <button 
                            onClick={() => setShowMonthDropdown(false)}
                            className="text-[#EAEAEA] hover:text-purple-300 hover:bg-purple-300/10 w-10 h-10 rounded-full flex items-center justify-center transition-all hover:scale-110 text-3xl font-bold flex-shrink-0"
                          >
                            ×
                          </button>
                        </div>
                      </div>
                      
                      {/* Calendrier des Mois en Grille - Avec padding bottom */}
                      <div className="p-4 sm:p-6 pb-6 sm:pb-8">
                        {/* Année en Cours */}
                        <div className="mb-4 sm:mb-6">
                          <h4 className="text-purple-300 font-black text-base sm:text-lg mb-3 sm:mb-4 flex items-center gap-2">
                            <span className="text-xl sm:text-2xl">🗓️</span>
                            {new Date().getFullYear()}
                          </h4>
                          <div className="grid grid-cols-3 sm:grid-cols-4 gap-2 sm:gap-3">
                            {[...Array(12)].map((_, i) => {
                              const monthIndex = i + 1;
                              const now = new Date();
                              const currentMonth = now.getMonth() + 1;
                              const currentYear = now.getFullYear();
                              
                              // Afficher UNIQUEMENT les mois déjà écoulés AVANT le mois précédent
                              // Exclure : mois en cours, mois précédent, et tous les mois futurs
                              const isExcluded = monthIndex >= (currentMonth - 1);
                              
                              if (isExcluded) return null;
                              
                              const monthName = new Date(currentYear, i, 1).toLocaleDateString('fr-FR', { month: 'long' });
                              const isSelected = customMonth?.year === currentYear && customMonth?.month === monthIndex;
                              
                              return (
                                <button
                                  key={monthIndex}
                                  onClick={() => {
                                    setCustomMonth({ year: currentYear, month: monthIndex });
                                    setSelectedMonth('custom');
                                    setShowMonthDropdown(false);
                                  }}
                                  className={`group relative px-3 sm:px-4 py-3 sm:py-4 rounded-lg sm:rounded-xl font-bold text-xs sm:text-sm md:text-base transition-all duration-300 transform hover:scale-105 ${
                                    isSelected
                                      ? 'bg-gradient-to-br from-purple-500 to-pink-500 text-white shadow-lg shadow-purple-400/50'
                                      : 'bg-gradient-to-br from-[#2A3B5C]/50 to-[#1C2541]/50 text-[#EAEAEA] border-2 border-purple-400/30 hover:border-purple-400/60 hover:bg-gradient-to-br hover:from-purple-400/20 hover:to-pink-400/20'
                                  }`}
                                >
                                  <div className="absolute inset-0 bg-gradient-to-r from-purple-400/0 via-purple-400/20 to-purple-400/0 opacity-0 group-hover:opacity-100 transition-opacity rounded-xl"></div>
                                  <span className="relative capitalize">{monthName}</span>
                                </button>
                              );
                            })}
                          </div>
                        </div>
                        
                        {/* Année Précédente (si applicable) */}
                        {new Date().getMonth() < 6 && (
                          <div className="pb-2">
                            <h4 className="text-purple-200 font-black text-base sm:text-lg mb-3 sm:mb-4 flex items-center gap-2">
                              <span className="text-xl sm:text-2xl">📆</span>
                              {new Date().getFullYear() - 1}
                            </h4>
                            <div className="grid grid-cols-3 sm:grid-cols-4 gap-2 sm:gap-3">
                              {[...Array(12)].map((_, i) => {
                                const monthIndex = i + 1;
                                const lastYear = new Date().getFullYear() - 1;
                                
                                // Pour l'année précédente, afficher TOUS les 12 mois
                                // car ils sont tous déjà écoulés
                                
                                const monthName = new Date(lastYear, i, 1).toLocaleDateString('fr-FR', { month: 'long' });
                                const isSelected = customMonth?.year === lastYear && customMonth?.month === monthIndex;
                                
                                return (
                                  <button
                                    key={`${lastYear}-${monthIndex}`}
                                    onClick={() => {
                                      setCustomMonth({ year: lastYear, month: monthIndex });
                                      setSelectedMonth('custom');
                                      setShowMonthDropdown(false);
                                    }}
                                    className={`group relative px-3 sm:px-4 py-3 sm:py-4 rounded-lg sm:rounded-xl font-bold text-xs sm:text-sm md:text-base transition-all duration-300 transform hover:scale-105 ${
                                      isSelected
                                        ? 'bg-gradient-to-br from-purple-500 to-pink-500 text-white shadow-lg shadow-purple-400/50'
                                        : 'bg-gradient-to-br from-[#2A3B5C]/50 to-[#1C2541]/50 text-[#EAEAEA] border-2 border-purple-400/30 hover:border-purple-400/60 hover:bg-gradient-to-br hover:from-purple-400/20 hover:to-pink-400/20'
                                    }`}
                                  >
                                    <div className="absolute inset-0 bg-gradient-to-r from-purple-400/0 via-purple-400/20 to-purple-400/0 opacity-0 group-hover:opacity-100 transition-opacity rounded-xl"></div>
                                    <span className="relative capitalize">{monthName}</span>
                                  </button>
                                );
                              })}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                  </div>
                </>
              )}

              {/* Mois Précédent - RESPONSIVE */}
              <button
                onClick={() => setSelectedMonth('previous')}
                className={`w-full sm:w-auto px-4 sm:px-6 py-3 sm:py-4 rounded-xl sm:rounded-2xl font-black text-sm sm:text-base transition-all duration-300 transform hover:scale-105 ${
                  selectedMonth === 'previous'
                    ? 'bg-gradient-to-r from-purple-500 via-pink-500 to-purple-500 text-white shadow-[0_0_40px_rgba(168,85,247,0.6)] scale-105'
                    : 'bg-gradient-to-br from-[#1C2541] to-[#2A3B5C] text-[#EAEAEA] border-2 border-purple-400/30 hover:border-purple-400/60'
                }`}
              >
                <span className="block sm:inline">📆 Mois Précédent</span>
                <div className="text-xs font-normal mt-1 opacity-80">
                  {new Date(new Date().getFullYear(), new Date().getMonth() - 1, 1).toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' })}
                </div>
              </button>

              {/* Mois en Cours - RESPONSIVE */}
              <button
                onClick={() => setSelectedMonth('current')}
                className={`w-full sm:w-auto px-4 sm:px-6 py-3 sm:py-4 rounded-xl sm:rounded-2xl font-black text-sm sm:text-base transition-all duration-300 transform hover:scale-105 ${
                  selectedMonth === 'current'
                    ? 'bg-gradient-to-r from-purple-500 via-pink-500 to-purple-500 text-white shadow-[0_0_40px_rgba(168,85,247,0.6)] scale-105'
                    : 'bg-gradient-to-br from-[#1C2541] to-[#2A3B5C] text-[#EAEAEA] border-2 border-purple-400/30 hover:border-purple-400/60'
                }`}
              >
                <span className="block sm:inline">📆 Mois en Cours</span>
                <div className="text-xs font-normal mt-1 opacity-80">
                  {new Date().toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' })}
                </div>
              </button>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Total Dépôts Card - ULTRA PREMIUM */}
      {!loading && depots.length > 0 && (
        <div className="relative group">
          <div className="absolute -inset-1 bg-gradient-to-r from-purple-500 via-pink-400 to-purple-500 rounded-3xl blur-xl group-hover:blur-2xl transition-all opacity-50 animate-pulse"></div>
          <Card className="relative backdrop-blur-2xl bg-gradient-to-br from-[#1C2541]/90 via-purple-900/10 to-[#1C2541]/90 border-3 border-purple-400/60 shadow-2xl hover:shadow-purple-400/40 hover:scale-[1.02] transition-all duration-500">
            <CardContent className="p-8">
              <div className="flex flex-col md:flex-row items-center justify-between gap-6">
                <div className="text-center md:text-left space-y-2">
                  <div className="flex items-center justify-center md:justify-start gap-3">
                    <TrendingUp className="w-10 h-10 text-purple-400 animate-bounce" />
                    <h2 className="text-3xl font-black text-transparent bg-clip-text bg-gradient-to-r from-purple-300 to-pink-200">
                      TOTAL DES DÉPÔTS
                    </h2>
                  </div>
                  <p className="text-purple-200/70 text-lg font-semibold">
                    {depots.length} dépôt{depots.length > 1 ? 's' : ''} enregistré{depots.length > 1 ? 's' : ''}
                  </p>
                </div>
                
                <div className="relative">
                  <div className="absolute inset-0 bg-gradient-to-r from-purple-400 to-pink-300 rounded-2xl blur-xl animate-pulse"></div>
                  <div className="relative bg-gradient-to-br from-purple-500/20 to-pink-500/20 backdrop-blur-xl rounded-2xl p-8 border-2 border-purple-300/50 shadow-2xl">
                    <div className="text-center">
                      <p className="text-purple-300 text-sm font-bold uppercase tracking-widest mb-2">Montant Total</p>
                      <div className="text-5xl md:text-6xl font-black text-transparent bg-clip-text bg-gradient-to-r from-purple-300 via-pink-200 to-purple-300 drop-shadow-[0_0_20px_rgba(192,132,252,0.8)] animate-pulse">
                        {totalDepots.toLocaleString()}
                      </div>
                      <p className="text-3xl font-black text-purple-400 mt-2">FCFA</p>
                    </div>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {loading ? (
        <div className="flex justify-center py-20">
          <div className="relative">
            <div className="w-24 h-24 border-8 border-purple-400/30 rounded-full"></div>
            <div className="w-24 h-24 border-8 border-purple-400 border-t-transparent rounded-full animate-spin absolute top-0"></div>
            <Sparkles className="w-8 h-8 text-purple-300 absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 animate-pulse" />
          </div>
        </div>
      ) : depots.length === 0 ? (
        <Card className="backdrop-blur-xl bg-[#1C2541]/80 border-2 border-purple-400/30">
          <CardContent className="p-12 text-center">
            <Sparkles className="w-16 h-16 text-purple-400/50 mx-auto mb-4" />
            <p className="text-purple-200/70 text-xl font-semibold">Aucun dépôt enregistré</p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-6">
          {depots.map((depot, index) => (
            <div key={depot.id} className="relative group" style={{ animationDelay: `${index * 50}ms` }}>
              {/* Glow effect */}
              <div className="absolute -inset-1 bg-gradient-to-r from-purple-400/20 via-pink-300/20 to-purple-400/20 rounded-2xl blur-xl group-hover:blur-2xl transition-all opacity-0 group-hover:opacity-100"></div>
              
              <Card className="relative backdrop-blur-xl bg-gradient-to-br from-[#1C2541]/90 via-[#2A3B5C]/70 to-[#1C2541]/90 border-2 border-purple-400/30 hover:border-purple-400/60 shadow-xl hover:shadow-2xl hover:shadow-purple-400/20 hover:scale-[1.01] transition-all duration-300">
                <CardContent className="p-6">
                  <div className="flex flex-col lg:flex-row items-stretch lg:items-center justify-between gap-6">
                    {/* Left side - Details */}
                    <div className="flex-1 space-y-3">
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        <div className="flex items-center gap-3 bg-purple-900/20 backdrop-blur-sm p-3 rounded-xl border border-purple-400/20">
                          <span className="text-2xl">📅</span>
                          <div>
                            <p className="text-purple-300/60 text-xs font-semibold uppercase">Date</p>
                            <p className="text-purple-100 font-bold">{new Date(depot.date).toLocaleDateString('fr-FR', { weekday: 'short', year: 'numeric', month: 'short', day: 'numeric' })}</p>
                          </div>
                        </div>
                        
                        <div className="flex items-center gap-3 bg-purple-900/20 backdrop-blur-sm p-3 rounded-xl border border-purple-400/20">
                          <span className="text-2xl">🏦</span>
                          <div>
                            <p className="text-purple-300/60 text-xs font-semibold uppercase">Banque</p>
                            <p className="text-purple-100 font-bold">{depot.banque || 'Non spécifiée'}</p>
                          </div>
                        </div>
                      </div>
                      
                      {depot.note && (
                        <div className="bg-gradient-to-r from-purple-900/30 to-pink-900/30 backdrop-blur-sm p-4 rounded-xl border border-purple-400/20">
                          <p className="text-purple-200/90 text-sm leading-relaxed">
                            <span className="text-purple-400 font-bold">📝 Note:</span> {depot.note}
                          </p>
                        </div>
                      )}
                      
                      {depot.effectueParNom && (
                        <div className="flex items-center gap-2 text-purple-300 font-bold">
                          <span className="text-xl">👤</span>
                          <span className="text-sm">Effectué par:</span>
                          <span className="text-purple-200">{depot.effectueParNom}</span>
                        </div>
                      )}
                    </div>
                    
                    {/* Right side - Amount & Actions */}
                    <div className="flex flex-col items-stretch gap-4">
                      {/* Amount Display */}
                      <div className="relative group/amount">
                        <div className="absolute inset-0 bg-gradient-to-br from-purple-400/30 to-pink-400/30 rounded-2xl blur-lg group-hover/amount:blur-xl transition-all"></div>
                        <div className="relative bg-gradient-to-br from-purple-500/10 to-pink-500/10 backdrop-blur-xl rounded-2xl p-6 border-2 border-purple-400/40 shadow-xl text-center min-w-[200px]">
                          <p className="text-purple-400 text-xs font-bold uppercase tracking-widest mb-2">Montant</p>
                          <div className="text-4xl font-black text-transparent bg-clip-text bg-gradient-to-r from-purple-300 via-pink-200 to-purple-300 drop-shadow-[0_0_15px_rgba(192,132,252,0.6)]">
                            {depot.montant.toLocaleString()}
                          </div>
                          <p className="text-xl font-black text-purple-400 mt-1">FCFA</p>
                        </div>
                      </div>
                      
                      {/* Action Buttons — CEO : uniquement admin peut Modifier/Supprimer */}
                      {user?.role === 'admin' && (
                        <div className="flex flex-col gap-2">
                          <Button
                            onClick={() => handleEdit(depot)}
                            size="sm"
                            className="bg-gradient-to-r from-amber-500 to-yellow-500 hover:from-amber-600 hover:to-yellow-600 text-[#0B132B] font-bold transition-all hover:scale-105 shadow-lg shadow-amber-400/30 border border-amber-300/50"
                          >
                            <Edit className="w-4 h-4 mr-2" />
                            Modifier
                          </Button>
                          <Button
                            onClick={() => {
                              setSelectedDepot(depot);
                              setShowDeleteDialog(true);
                            }}
                            size="sm"
                            className="bg-gradient-to-r from-rose-600 to-red-600 hover:from-rose-700 hover:to-red-700 text-white font-bold transition-all hover:scale-105 shadow-lg shadow-rose-500/30"
                          >
                            <Trash2 className="w-4 h-4 mr-2" />
                            Supprimer
                          </Button>
                        </div>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          ))}
        </div>
      )}

      {/* Add/Edit Dialog */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="backdrop-blur-2xl bg-gradient-to-br from-[#1C2541]/95 to-[#2A3B5C]/95 border-2 border-purple-400/40 text-[#EAEAEA] shadow-2xl">
          <DialogHeader>
            <DialogTitle className="text-2xl font-black text-transparent bg-clip-text bg-gradient-to-r from-purple-300 to-pink-200 flex items-center gap-2">
              <DollarSign className="w-6 h-6 text-purple-400" />
              {isEditing ? 'Modifier le Dépôt' : 'Nouveau Dépôt'}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label className="text-purple-300 font-semibold">Montant (FCFA) *</Label>
              <Input
                type="number"
                required
                value={formData.montant}
                onChange={(e) => setFormData({ ...formData, montant: e.target.value })}
                className="bg-[#0B132B]/80 border-purple-400/30 text-[#EAEAEA] focus:border-purple-400 transition-colors text-lg font-bold"
                placeholder="0"
              />
            </div>
            
            <div>
              <Label className="text-purple-300 font-semibold">Date *</Label>
              {isMobile ? (
                <input
                  type="date"
                  value={formData.date}
                  onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                  className="w-full bg-[#0B132B]/80 border-2 border-purple-400/30 text-[#EAEAEA] focus:border-purple-400 transition-colors text-base font-bold rounded-lg px-4 py-2.5 cursor-pointer"
                  data-testid="depots-date-mobile"
                  style={{ colorScheme: 'dark' }}
                />
              ) : (
                <DatePicker
                  selected={new Date(formData.date)}
                  onChange={(date) => {
                    const formattedDate = date.toISOString().split('T')[0];
                    setFormData({ ...formData, date: formattedDate });
                  }}
                  dateFormat="dd/MM/yyyy"
                  locale="fr"
                  className="w-full bg-[#0B132B]/80 border-2 border-purple-400/30 text-[#EAEAEA] focus:border-purple-400 transition-colors text-base font-bold rounded-lg px-4 py-2 cursor-pointer"
                  calendarClassName="calendar-premium-popup"
                  showPopperArrow={false}
                  popperPlacement="bottom-start"
                  portalId="datepicker-portal-root"
                  popperProps={{ strategy: 'fixed' }}
                />
              )}
            </div>
            
            <div>
              <Label className="text-purple-300 font-semibold">Banque</Label>
              <Input
                value={formData.banque}
                onChange={(e) => setFormData({ ...formData, banque: e.target.value })}
                className="bg-[#0B132B]/80 border-purple-400/30 text-[#EAEAEA] focus:border-purple-400 transition-colors"
                placeholder="Nom de la banque"
              />
            </div>
            
            <div>
              <Label className="text-purple-300 font-semibold">Note</Label>
              <Textarea
                value={formData.note}
                onChange={(e) => setFormData({ ...formData, note: e.target.value })}
                className="bg-[#0B132B]/80 border-purple-400/30 text-[#EAEAEA] focus:border-purple-400 transition-colors"
                placeholder="Description optionnelle"
                rows={3}
              />
            </div>
            
            <div className="flex gap-3 pt-4">
              <Button
                type="button"
                onClick={() => setIsDialogOpen(false)}
                className="flex-1 bg-[#1C2541] hover:bg-purple-400/20 text-[#EAEAEA] font-bold border border-purple-400/30 hover:border-purple-400/60 transition-all"
              >
                Annuler
              </Button>
              <Button
                type="submit"
                className="flex-1 bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 text-white font-black text-lg py-6 shadow-xl shadow-purple-400/40 hover:scale-105 transition-all"
              >
                {isEditing ? 'Modifier' : 'Enregistrer'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <DialogContent className="backdrop-blur-2xl bg-gradient-to-br from-[#1C2541]/95 to-[#2A3B5C]/95 border-2 border-rose-500/40 text-[#EAEAEA] shadow-2xl">
          <DialogHeader>
            <DialogTitle className="text-2xl font-black text-rose-400 flex items-center gap-2">
              <Trash2 className="w-6 h-6" />
              Confirmer la suppression
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-[#EAEAEA] text-lg">
              Êtes-vous sûr de vouloir supprimer ce dépôt ?
            </p>
            {selectedDepot && (
              <div className="bg-[#0B132B]/80 p-4 rounded-xl border-2 border-purple-400/30">
                <p className="text-purple-300 font-black text-xl">{selectedDepot.banque || 'Dépôt bancaire'}</p>
                <p className="text-purple-100 text-2xl font-bold mt-2">{selectedDepot.montant.toLocaleString()} FCFA</p>
                <p className="text-purple-200/70 text-sm mt-1">{new Date(selectedDepot.date).toLocaleDateString('fr-FR')}</p>
              </div>
            )}
            <div className="flex gap-3">
              <Button
                onClick={() => setShowDeleteDialog(false)}
                className="flex-1 bg-[#1C2541] hover:bg-purple-400/20 text-[#EAEAEA] font-bold border border-purple-400/30 hover:border-purple-400/60 transition-all"
              >
                Annuler
              </Button>
              <Button
                onClick={handleDelete}
                className="flex-1 bg-gradient-to-r from-rose-600 to-red-600 hover:from-rose-700 hover:to-red-700 text-white font-bold shadow-lg shadow-rose-500/40 hover:scale-105 transition-all"
              >
                Supprimer
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Depots;
